import { useState, useCallback, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { ApiTokenManagementRow, ApiTokenType } from '../ApiTokenManagementRow'
import { useExternalServiceApiTokenRepository } from '../../../../services/adaptors/externalServiceApiTokenRepositoryAdaptor'
import { useUserApiTokenRepository } from '../../../../services/adaptors/userApiTokenRepositoryAdaptor'
import { CreateExternalServiceApiTokenInput } from '../../../../domain/entity/ExternalServiceApiTokenEntity'
import { ApiTokenScope } from '../../../../domain/entity/ApiTokenScope'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'

type CreateTokenInput = {
  serviceName: string
  expiredAt: number
  scopes: string[]
}

type UseApiTokenManagementDataReturn = {
  rowData: ApiTokenManagementRow[]
  availableScopes: ApiTokenScope[]
  loading: boolean
  onReload: () => Promise<void>
  onCreateServiceToken: (input: CreateTokenInput) => Promise<string | null>
  onRevokeToken: (uuid: string, tokenType: ApiTokenType) => Promise<boolean>
}

export const useApiTokenManagementData = (
  functionUuid: string
): UseApiTokenManagementDataReturn => {
  const [rowData, setRowData] = useState<ApiTokenManagementRow[]>([])
  const [availableScopes, setAvailableScopes] = useState<ApiTokenScope[]>([])
  const [loading, setLoading] = useState(false)
  const serviceTokenRepository = useExternalServiceApiTokenRepository()
  const userTokenRepository = useUserApiTokenRepository()

  const onReload = useCallback(async () => {
    setLoading(true)
    try {
      const [serviceTokens, userTokens, scopes] = await Promise.all([
        serviceTokenRepository.getAll(),
        userTokenRepository.getAll(),
        serviceTokenRepository.getScopes(),
      ])

      const serviceRows: ApiTokenManagementRow[] = serviceTokens.map(token => ({
        uuid: token.uuid,
        tokenType: 'service' as const,
        name: token.serviceName,
        createdBy: token.createdBy,
        scopes: token.scopes,
        expiredAt: token.expiredAt,
        createdAt: token.createdAt,
        revoked: token.revoked,
        expired: token.expired,
      }))

      const userRows: ApiTokenManagementRow[] = userTokens.map(token => ({
        uuid: token.uuid,
        tokenType: 'user' as const,
        name: token.tokenName,
        owner: token.owner,
        createdBy: token.createdBy,
        scopes: token.scopes,
        expiredAt: token.expiredAt,
        createdAt: token.createdAt,
        lastUsedAt: token.lastUsedAt,
        revoked: token.revoked,
        expired: token.expired,
      }))

      setRowData([...serviceRows, ...userRows])
      setAvailableScopes(scopes.sort((a, b) => a.sortOrder - b.sortOrder))
    } catch (error) {
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.ERROR,
          title: intl.formatMessage({
            id: 'apiTokenManagement.error.load',
          }),
        })
      )
    } finally {
      setLoading(false)
    }
  }, [serviceTokenRepository, userTokenRepository, functionUuid])

  const onCreateServiceToken = useCallback(
    async ({
      serviceName,
      expiredAt,
      scopes,
    }: CreateTokenInput): Promise<string | null> => {
      setLoading(true)
      try {
        const createInput: CreateExternalServiceApiTokenInput = {
          uuid: uuidv4(),
          serviceName,
          expiredAt,
          scopes,
        }
        const { rawToken } = await serviceTokenRepository.create(createInput)
        await onReload()
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: 'apiTokenManagement.success.createService',
            }),
          })
        )
        return rawToken
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        const isDuplicate = errorMessage.includes('duplicated')
        if (isDuplicate) {
          store.dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.WARN,
              title: intl.formatMessage({
                id: 'apiTokenManagement.error.duplicateServiceName',
              }),
            })
          )
        } else {
          store.dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.ERROR,
              title: intl.formatMessage({
                id: 'apiTokenManagement.error.createService',
              }),
            })
          )
        }
        return null
      } finally {
        setLoading(false)
      }
    },
    [serviceTokenRepository, functionUuid, onReload]
  )

  const onRevokeToken = useCallback(
    async (uuid: string, tokenType: ApiTokenType): Promise<boolean> => {
      setLoading(true)
      try {
        if (tokenType === 'service') {
          await serviceTokenRepository.revoke(uuid)
        } else {
          await userTokenRepository.revoke(uuid)
        }
        await onReload()
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({
              id: 'apiTokenManagement.success.revoke',
            }),
          })
        )
        return true
      } catch (error) {
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.ERROR,
            title: intl.formatMessage({
              id: 'apiTokenManagement.error.revoke',
            }),
          })
        )
        return false
      } finally {
        setLoading(false)
      }
    },
    [serviceTokenRepository, userTokenRepository, functionUuid, onReload]
  )

  useEffect(() => {
    onReload()
  }, [onReload])

  return {
    rowData,
    availableScopes,
    loading,
    onReload,
    onCreateServiceToken,
    onRevokeToken,
  }
}
